<script setup>
/**
 * 一个用户的账本。**从调用详情的「计费」块点进来** —— 那里只给得出一个流水行 id,
 * 回答不了「这次扣完之后余额为什么是这个数」。
 *
 * 两张表之间只有一条桥:`billing_ledger.ref_type = 'llm_chat' AND ref_id = llm_call_log.request_id`。
 * 所以这张表的「关联」列点回去,就是那次调用。
 */
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Badge, Button, EmptyState, PageHeader, Section, Spinner, Table } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { listUserLedger } from '../../api/llm/index.js'
import { formatDateTime, formatInt, formatMoney } from './llm-format.js'

const props = defineProps({
  /** 路由参数。 */
  userId: { type: String, required: true },
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

/** 从哪一条点进来的 —— 那一行高亮,不然一屏流水里找不到它。 */
const highlight = computed(() => String(route.query.highlight ?? ''))

const entries = ref([])
const nextCursor = ref(null)
const cursors = ref([])
const loading = ref(false)
const error = ref('')

const columns = computed(() => [
  { key: 'created_at', label: t('visualGameAdmin.llm.ledger.table.time'), width: '180px', mono: true },
  { key: 'type', label: t('visualGameAdmin.llm.ledger.table.type'), width: '100px' },
  { key: 'reason', label: t('visualGameAdmin.llm.ledger.table.reason'), width: '120px' },
  { key: 'amount', label: t('visualGameAdmin.llm.ledger.table.amount'), width: '130px', align: 'right', mono: true },
  {
    key: 'balance_after',
    label: t('visualGameAdmin.llm.ledger.table.balanceAfter'),
    width: '140px',
    align: 'right',
    mono: true,
  },
  { key: 'call', label: t('visualGameAdmin.llm.ledger.table.call'), width: '320px' },
])

const rows = computed(() =>
  entries.value.map((entry) => ({
    ...entry,
    is_highlighted: entry.id === highlight.value,
    time: formatDateTime(entry.created_at),
    // 有符号:扣款是负数，充值是正数。前面带号，人一眼看得出方向。
    money: `${entry.amount < 0 ? '-' : '+'}${formatMoney(Math.abs(entry.amount))}`,
    balance: formatMoney(entry.balance_after),
  })),
)

watch(() => props.userId, reset, { immediate: true })

function reset() {
  cursors.value = []
  load(null)
}

async function load(cursor) {
  loading.value = true
  error.value = ''
  try {
    const page = await listUserLedger({ user_id: props.userId, limit: 50, cursor })
    entries.value = page.items
    nextCursor.value = page.next_cursor
  } catch (err) {
    // 后端的错原样说出来，不吞成「加载失败」。
    error.value = err.message
  } finally {
    loading.value = false
  }
}

function nextPage() {
  cursors.value.push(nextCursor.value)
  load(nextCursor.value)
}

function prevPage() {
  cursors.value.pop()
  load(cursors.value[cursors.value.length - 1] ?? null)
}

/** 点回那次调用。调用日志按 request_id 查是唯一键,一次调用就一行。 */
function openCall(requestId) {
  router.push({ name: 'llm-calls', query: { request_id: requestId } })
}
</script>

<template>
  <div class="vga-page" data-insp-path="apps/visual-game-admin/src/pages/llm/Ledger.vue#page">
    <PageHeader :title="userId">
      <template #actions>
        <Button
          variant="secondary"
          @click="router.push({ name: 'llm-calls', query: { user_id: userId } })"
        >
          {{ t('visualGameAdmin.llm.ledger.gotoCalls') }}
        </Button>
      </template>
    </PageHeader>

    <Section
      :padded="false"
      data-insp-path="apps/visual-game-admin/src/pages/llm/Ledger.vue#table"
    >
      <div
        v-if="loading"
        class="ledger__state"
      >
        <Spinner :label="t('visualGameAdmin.common.loading')" />
      </div>

      <p
        v-else-if="error"
        class="vga-error ledger__state"
        role="alert"
      >
        {{ error }}
      </p>

      <EmptyState
        v-else-if="entries.length === 0"
        :title="t('visualGameAdmin.llm.ledger.empty')"
      />

      <template v-else>
        <Table
          :columns="columns"
          :rows="rows"
          row-key="id"
        >
          <template #created_at="{ row }">
            <span :class="{ 'is-highlighted': row.is_highlighted }">{{ row.time }}</span>
          </template>
          <template #type="{ row }">
            <Badge :variant="row.amount < 0 ? 'neutral' : 'success'">{{ row.type }}</Badge>
          </template>
          <template #reason="{ row }">
            {{ row.reason ?? '—' }}
          </template>
          <template #amount="{ row }">
            <span :class="row.amount < 0 ? 'is-debit' : 'is-credit'">{{ row.money }}</span>
          </template>
          <template #balance_after="{ row }">
            {{ row.balance }}
          </template>
          <template #call="{ row }">
            <button
              v-if="row.call"
              class="ledger__call"
              type="button"
              @click="openCall(row.call.request_id)"
            >
              {{ row.call.model }}
              <span class="ledger__call-meta">
                {{ formatInt(row.call.prompt_tokens) }} / {{ formatInt(row.call.completion_tokens) }}
              </span>
            </button>
            <span v-else>—</span>
          </template>
        </Table>

        <div class="ledger__pager">
          <Button
            variant="ghost"
            size="sm"
            :disabled="cursors.length === 0"
            @click="prevPage"
          >
            {{ t('visualGameAdmin.llm.calls.pagination.prev') }}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            :disabled="!nextCursor"
            @click="nextPage"
          >
            {{ t('visualGameAdmin.llm.calls.pagination.next') }}
          </Button>
        </div>
      </template>
    </Section>
  </div>
</template>

<style scoped>
.ledger__state {
  display: grid;
  justify-items: center;
  gap: var(--drama-space-md);
  padding: var(--drama-space-2xl);
}

.is-highlighted {
  color: var(--drama-color-primary);
  font-weight: var(--drama-font-weight-medium);
}

.is-debit {
  color: var(--drama-color-text);
}

.is-credit {
  color: var(--drama-color-success);
}

.ledger__call {
  display: flex;
  align-items: baseline;
  gap: var(--drama-space-sm);
  padding: 0;
  border: none;
  background: none;
  color: var(--drama-color-text);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-sm);
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.ledger__call-meta {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.ledger__pager {
  display: flex;
  justify-content: flex-end;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-md) var(--drama-space-xl);
  border-top: 1px solid var(--drama-color-border);
}
</style>
